import React, { useState, useMemo, useEffect } from 'react';
import { Feather, Heart, Search, Inbox, MessageCircle, ChevronLeft, ChevronRight, ChevronDown, X, Minus, Plus, Type, RefreshCw } from 'lucide-react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../hooks/useFirebase';
import { APP_ID } from '../../constants/config';

// Robust date parser to handle standard YYYY-MM-DD, ISO, and Notion CSV formats
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return new Date(trimmed + 'T12:00:00');
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
};

const getSortTime = (p) => {
  if (p.date) {
    const d = safeParseDate(p.date);
    if (d) return d.getTime();
  }
  if (p.year) {
    const d = new Date(p.year.toString());
    if (!isNaN(d.getTime())) return d.getTime();
  }
  return 0;
};

const PoemsSection = ({ poems, theme, isDarkMode, handleNavigate }) => {
  const [localLikes, setLocalLikes] = useState(() => JSON.parse(localStorage.getItem('likedPoems') || '[]'));
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedYear, setSelectedYear] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [selectedTag, setSelectedTag] = useState('All');
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [poemFontSize, setPoemFontSize] = useState(1.1);
  
  const pillStyle = { borderRadius: '9999px' };
  const cardStyle = { borderRadius: '32px' };

  // Dynamically extract available Years, Months, and Tags based on the active filters
  const { availableYears, availableMonths, availableTags, yearCounts, monthCounts, tagCounts } = useMemo(() => {
    const yearCounts = {};
    const monthCounts = {};
    const tagCounts = {};
    let totalYrs = 0;
    let totalMths = 0;
    let totalTgs = 0;
    
    poems.forEach(p => {
      const dateObj = safeParseDate(p.date);
      const pYear = dateObj ? dateObj.getFullYear().toString() : (p.year?.toString() || '');
      const pMonth = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
      let pTags = [];
      if (p.tags) {
        pTags = Array.isArray(p.tags) ? p.tags : p.tags.split(',').map(s => s.trim());
      }
      
      const matchY = selectedYear === 'All' || pYear === selectedYear;
      const matchM = selectedMonth === 'All' || pMonth === selectedMonth;
      const matchT = selectedTag === 'All' || pTags.includes(selectedTag);

      if (matchM && matchT && pYear) {
        yearCounts[pYear] = (yearCounts[pYear] || 0) + 1;
        totalYrs++;
      }
      if (matchY && matchT && pMonth) {
        monthCounts[pMonth] = (monthCounts[pMonth] || 0) + 1;
        totalMths++;
      }
      if (matchY && matchM) {
        pTags.forEach(t => { if (t) tagCounts[t] = (tagCounts[t] || 0) + 1; });
        totalTgs++;
      }
    });
    
    yearCounts['All'] = totalYrs;
    monthCounts['All'] = totalMths;
    tagCounts['All'] = totalTgs;

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const sortedMonths = Object.keys(monthCounts).filter(k => k !== 'All').sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));
    
    return {
      availableYears: Object.keys(yearCounts).filter(k => k !== 'All').sort((a, b) => b - a),
      availableMonths: sortedMonths,
      availableTags: Object.keys(tagCounts).filter(k => k !== 'All').sort(),
      yearCounts,
      monthCounts,
      tagCounts
    };
  }, [poems, selectedYear, selectedMonth, selectedTag]);

  const displayYears = selectedYear !== 'All' && !availableYears.includes(selectedYear) ? [selectedYear, ...availableYears] : availableYears;
  const displayMonths = selectedMonth !== 'All' && !availableMonths.includes(selectedMonth) ? [selectedMonth, ...availableMonths] : availableMonths;
  const displayTags = selectedTag !== 'All' && !availableTags.includes(selectedTag) ? [selectedTag, ...availableTags] : availableTags;

  const filteredPoems = useMemo(() => {
    let result = poems;
    
    // 1. Search Query Filter
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter(p => 
        (p.text && p.text.toLowerCase().includes(lowerQuery)) ||
        (p.title && p.title.toLowerCase().includes(lowerQuery)) ||
        (p.author && p.author.toLowerCase().includes(lowerQuery)) ||
        (p.tags && (Array.isArray(p.tags) ? p.tags.join(' ') : p.tags).toLowerCase().includes(lowerQuery))
      );
    }

    // 2. Category/Time Filters
    if (selectedYear !== 'All' || selectedMonth !== 'All' || selectedTag !== 'All') {
      result = result.filter(p => {
        const dateObj = safeParseDate(p.date);
        const year = dateObj ? dateObj.getFullYear().toString() : (p.year?.toString() || '');
        const month = dateObj ? dateObj.toLocaleString('en-US', { month: 'long' }) : '';
        let pTags = [];
        if (p.tags) {
          pTags = Array.isArray(p.tags) ? p.tags : p.tags.split(',').map(s => s.trim());
        }
        
        const matchYear = selectedYear === 'All' || year === selectedYear;
        const matchMonth = selectedMonth === 'All' || month === selectedMonth;
        const matchTag = selectedTag === 'All' || pTags.includes(selectedTag);

        return matchYear && matchMonth && matchTag;
      });
    }

    // 3. Sort Order
    return [...result].sort((a, b) => sortOrder === 'newest' ? getSortTime(b) - getSortTime(a) : getSortTime(a) - getSortTime(b));
  }, [poems, selectedYear, selectedMonth, selectedTag, searchQuery, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedMonth, selectedTag, searchQuery, sortOrder]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const totalPages = Math.ceil(filteredPoems.length / itemsPerPage);
  const paginatedPoems = filteredPoems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const hasActiveFilters = searchQuery !== '' || sortOrder !== 'newest' || selectedYear !== 'All' || selectedMonth !== 'All' || selectedTag !== 'All';

  const handleResetFilters = () => {
    setSearchQuery('');
    setSortOrder('newest');
    setSelectedYear('All');
    setSelectedMonth('All');
    setSelectedTag('All');
  };

  const handleLike = async (e, poemId) => {
    e.stopPropagation();
    const isLiked = localLikes.includes(poemId);
    
    const newLikes = isLiked ? localLikes.filter(id => id !== poemId) : [...localLikes, poemId];
    setLocalLikes(newLikes);
    localStorage.setItem('likedPoems', JSON.stringify(newLikes));

    try {
      await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'poems', poemId), {
        likes: increment(isLiked ? -1 : 1)
      });
    } catch (error) {
      console.error("Failed to like poem:", error);
    }
  };

  const handleWhatsAppShare = (e, poem) => {
    e.stopPropagation();
    const url = `${window.location.origin}/?poem=${poem.id}`;
    const shareText = `*${poem.title || 'Sajak'}*\n\n"${poem.text}"\n— ${poem.author || 'Ben Hasan'}\n\n${url}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  return (
    <section className="pt-32 pb-24 px-4 md:px-6 min-h-screen animate-in fade-in slide-in-from-bottom-4 duration-1000">
      <div className="max-w-4xl mx-auto w-full">
        
        <style>{`
          @keyframes staggerFadeUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .stagger-item {
            animation: staggerFadeUp 0.6s ease-out both;
          }
        `}</style>
        
        {/* Header Section with Search */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <h2 className={`text-5xl md:text-6xl font-serif ${theme.text}`}>Poetry Archive</h2>
            <p className={`max-w-xl text-sm md:text-base opacity-60 leading-relaxed ${theme.textMuted}`}>
              A dedicated sanctuary of metrical compositions, verses, and lingering whispers of reflection.
            </p>
          </div>
          
          {/* Search Pill Input */}
          <div 
            style={pillStyle}
            className={`relative flex items-center w-full md:w-64 px-6 py-3 border transition-all duration-500 ${
              isDarkMode 
                ? 'bg-stone-900/40 border-stone-800 focus-within:border-stone-600' 
                : 'bg-stone-50/50 border-stone-100 focus-within:border-stone-300'
            }`}
          >
            <Search size={14} className="opacity-40" />
            <input 
              type="text" 
              placeholder="Search Poems..." 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              className={`ml-3 w-full bg-transparent text-xs font-medium outline-none placeholder:opacity-40 ${theme.text}`}
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')} 
                className="ml-2 opacity-40 hover:opacity-100"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        
        {/* Interactive Filter Pills */}
        <div className="flex items-center gap-3 overflow-x-auto pb-4 mb-10 no-scrollbar">
          {/* Sort Button */}
          <button 
            onClick={() => setSortOrder(prev => prev === 'newest' ? 'oldest' : 'newest')}
            style={pillStyle}
            className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap ${
              isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400'
            }`}
          >
            {sortOrder === 'newest' ? 'Sort: Newest' : 'Sort: Oldest'}
          </button>

          <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

          {/* Year Filter */}
          {displayYears.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsYearOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedYear !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedYear === 'All' ? 'Year' : selectedYear}</span>
                <ChevronDown size={14} className={`${isYearOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isYearOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsYearOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Year</p>
                      {['All', ...displayYears].map(y => (
                        <button key={y} onClick={() => { setSelectedYear(y); setIsYearOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedYear === y ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{y === 'All' ? 'All Years' : y}</span>
                          <span className="opacity-40 text-[10px]">{yearCounts[y] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Month Filter */}
          {displayMonths.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsMonthOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedMonth !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedMonth === 'All' ? 'Month' : selectedMonth}</span>
                <ChevronDown size={14} className={`${isMonthOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isMonthOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsMonthOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Month</p>
                      {['All', ...displayMonths].map(m => (
                        <button key={m} onClick={() => { setSelectedMonth(m); setIsMonthOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedMonth === m ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{m === 'All' ? 'All Months' : m}</span>
                          <span className="opacity-40 text-[10px]">{monthCounts[m] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tag Filter */}
          {displayTags.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setIsTagOpen(prev => !prev)}
                style={pillStyle}
                className={`px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                  selectedTag !== 'All'
                    ? (isDarkMode ? 'bg-white text-stone-900 border-white' : 'bg-stone-900 text-white border-stone-900')
                    : (isDarkMode ? 'border-stone-800 text-stone-500 hover:border-stone-600' : 'border-stone-200 text-stone-400 hover:border-stone-400')
                }`}
              >
                <span>{selectedTag === 'All' ? 'Tag' : selectedTag}</span>
                <ChevronDown size={14} className={`${isTagOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>
              {isTagOpen && (
                <div className="fixed inset-0 z-50 p-4 animate-in fade-in zoom-in-95 duration-300">
                  <div className="absolute inset-0 bg-black/20 backdrop-blur-md" onClick={() => setIsTagOpen(false)} />
                  <div style={cardStyle} className={`relative w-full max-w-sm mx-auto mt-32 p-6 shadow-2xl border max-h-[60vh] overflow-y-auto ${isDarkMode ? 'bg-stone-900 border-stone-800' : 'bg-white border-stone-100'}`}>
                    <div className="flex flex-col gap-2">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-60 px-4 mb-4 ${theme.text}`}>Filter by Tag</p>
                      {['All', ...displayTags].map(t => (
                        <button key={t} onClick={() => { setSelectedTag(t); setIsTagOpen(false); }} style={pillStyle} className={`w-full text-left px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-all flex justify-between items-center ${selectedTag === t ? (isDarkMode ? 'bg-stone-800 text-white' : 'bg-stone-900 text-white') : `opacity-60 hover:opacity-100 ${theme.text}`}`}>
                          <span>{t === 'All' ? 'All Tags' : t}</span>
                          <span className="opacity-40 text-[10px]">{tagCounts[t] || 0}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className={`w-px h-5 flex-shrink-0 mx-2 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />

          {/* Font Size Controls */}
          <div style={pillStyle} className={`flex items-center px-2 py-1 border flex-shrink-0 transition-colors ${isDarkMode ? 'border-stone-800' : 'border-stone-200'}`}>
            <button onClick={() => setPoemFontSize(prev => Math.max(0.8, prev - 0.05))} className={`p-1.5 rounded-full transition-all ${isDarkMode ? 'text-stone-500 hover:bg-stone-800 hover:text-stone-300' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'}`} title="Decrease Font Size"><Minus size={14} /></button>
            <span className={`flex items-center justify-center px-2 opacity-50 ${theme.text}`} title="Font Size"><Type size={12} /></span>
            <button onClick={() => setPoemFontSize(prev => Math.min(3, prev + 0.05))} className={`p-1.5 rounded-full transition-all ${isDarkMode ? 'text-stone-500 hover:bg-stone-800 hover:text-stone-300' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-600'}`} title="Increase Font Size"><Plus size={14} /></button>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={handleResetFilters}
              style={pillStyle}
              className={`flex-shrink-0 px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-all duration-500 border whitespace-nowrap flex items-center gap-2 ${
                isDarkMode ? 'border-red-900/30 text-red-400 hover:bg-red-900/20' : 'border-red-100 text-red-500 hover:bg-red-50'
              }`}
            >
              <RefreshCw size={14} /> <span>Reset</span>
            </button>
          )}
        </div>
        
        {/* Native CSS Masonry Grid */}
        {paginatedPoems.length > 0 ? (
          <>
            <div key={`poem-page-${currentPage}`} className="columns-1 md:columns-2 gap-6 w-full">
              {paginatedPoems.map((poem, index) => (
              <div 
                key={poem.id} 
                onClick={() => handleNavigate('single-poem', null, null, poem.id)} 
                style={{ 
                  ...cardStyle, 
                  animationDelay: `${index * 100}ms`
                }}
                className={`stagger-item break-inside-avoid mb-6 p-8 border transition-colors transition-shadow transition-transform duration-700 cursor-pointer group ${
                  isDarkMode 
                    ? 'bg-stone-900/20 border-stone-800 hover:bg-stone-800/40 hover:border-stone-700' 
                    : 'bg-stone-50/50 border-stone-100 hover:bg-white hover:border-stone-200 shadow-sm hover:shadow-xl hover:-translate-y-1'
                }`}
              >
                <Feather size={20} className={`mb-4 opacity-20 transition-all duration-500 group-hover:-translate-y-1 ${theme.text}`} />
                
                {/* Poem Title */}
                {poem.title && (
                  <h3 className={`font-serif font-bold tracking-wider text-sm mb-4 uppercase opacity-80 ${theme.text}`}>
                    {poem.title}
                  </h3>
                )}
                
                {/* Poem Verses with Pre-Line to support carriage newlines */}
                <p 
                  className={`font-serif italic leading-relaxed mb-6 transition-all duration-300 whitespace-pre-line ${theme.text}`}
                  style={{ fontSize: `${poemFontSize}rem` }}
                >
                  {poem.text || 'Verse whispers loading...'}
                </p>

                <div className="flex flex-col gap-2 pt-4 border-t border-stone-100 dark:border-stone-900">
                  <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-60 ${theme.text}`}>— {poem.author || 'Ben Hasan'}</p>
                  {(poem.date || poem.year) && (
                    <p className={`text-[9px] font-bold uppercase tracking-[0.2em] opacity-40 ${theme.text}`}>
                      {poem.date 
                        ? (safeParseDate(poem.date) ? safeParseDate(poem.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : poem.date) 
                        : poem.year}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <p className={`text-[8px] uppercase tracking-widest opacity-30 ${theme.text}`}>{poem.tags ? (Array.isArray(poem.tags) ? poem.tags.join(', ') : poem.tags) : ''}</p>
                    
                    <div className="flex items-center gap-4">
                      {/* WhatsApp Share Button */}
                      <button 
                        onClick={(e) => handleWhatsAppShare(e, poem)}
                        className={`opacity-40 group-hover:opacity-100 hover:text-emerald-500 transition-all duration-300 z-10 ${theme.text}`}
                        title="Share to WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </button>
                      
                      {/* Like Button */}
                      <button 
                        onClick={(e) => handleLike(e, poem.id)}
                        className={`flex items-center gap-1.5 transition-all duration-300 z-10 ${localLikes.includes(poem.id) ? 'opacity-100 text-red-500' : `opacity-40 group-hover:opacity-100 hover:text-red-500 ${theme.text}`}`}
                      >
                        <Heart size={14} className={localLikes.includes(poem.id) ? "fill-red-500 text-red-500" : ""} />
                        <span className="text-[10px] font-bold">{poem.likes || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-12 pt-8 border-t border-stone-100 dark:border-stone-900 w-full">
                <button 
                  onClick={() => {
                    setCurrentPage(p => Math.max(1, p - 1));
                  }} 
                  disabled={currentPage === 1} 
                  style={pillStyle}
                  className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                    currentPage === 1 
                      ? 'opacity-10 cursor-not-allowed border-transparent' 
                      : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                  }`}
                >
                  <ChevronLeft size={16} /> <span>Prev</span>
                </button>
                
                <span className={`text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 ${theme.text}`}>
                  {currentPage} / {totalPages}
                </span>
                
                <button 
                  onClick={() => {
                    setCurrentPage(p => Math.min(totalPages, p + 1));
                  }} 
                  disabled={currentPage === totalPages} 
                  style={pillStyle}
                  className={`flex items-center space-x-3 px-6 py-3 text-[10px] font-bold uppercase tracking-widest transition-all border ${
                    currentPage === totalPages 
                      ? 'opacity-10 cursor-not-allowed border-transparent' 
                      : `border-transparent hover:border-stone-200 dark:hover:border-stone-800 ${theme.text}`
                  }`}
                >
                  <span>Next</span> <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="p-20 text-center opacity-20"><Inbox size={48} className="mx-auto mb-4" /><p className="text-[10px] font-bold uppercase tracking-widest">No poems found.</p></div>
        )}
      </div>
    </section>
  );
};

export default PoemsSection;
